import { Routes } from '@common/constants';
import request from '@utils/request';
import { Dispatch } from 'redux';
import { PaymentProviders } from 'shared-types';

import { wait } from '../../basket/utils';
import { ActionCreator, ActionCreatorWithPayload } from '../../types';

import {
  MAKE_PAYMENT_CARD_DECLINED,
  MAKE_PAYMENT_FAILURE,
  MAKE_PAYMENT_REQUEST,
  MAKE_PAYMENT_SUCCESS,
  MAKE_PAYMENT_UNKNOWN_STATE,
} from './action-types';
import {
  MakePaymentCardDeclinedStateAction,
  MakePaymentFailureAction,
  MakePaymentRequestAction,
  MakePaymentSuccessAction,
  MakePaymentType,
  MakePaymentUnknownStateAction,
} from './types';

const getMakePaymentSuccess: ActionCreator<MakePaymentSuccessAction> = () => ({
  type: MAKE_PAYMENT_SUCCESS,
});

export const makePaymentSuccess = getMakePaymentSuccess;

const getMakePaymentRequest: ActionCreator<MakePaymentRequestAction> = () => ({
  type: MAKE_PAYMENT_REQUEST,
});

const getMakePaymentFailure: ActionCreator<MakePaymentFailureAction> = () => ({
  type: MAKE_PAYMENT_FAILURE,
});

const getMakePaymentUnknownState: ActionCreator<MakePaymentUnknownStateAction> = () => ({
  type: MAKE_PAYMENT_UNKNOWN_STATE,
});

const getMakePaymentCardDeclined: ActionCreatorWithPayload<
  MakePaymentCardDeclinedStateAction,
  string
> = (clientSecret) => ({
  type: MAKE_PAYMENT_CARD_DECLINED,
  payload: clientSecret,
});

const awaitOrderStatus = async (
  propertyId: number,
  orderNumber: string,
  moneyTransferQueueId: string,
  paymentMethod: string | null | undefined,
  dispatch: Dispatch,
  pollCount = 1
) => {
  const timeout = 60;

  if (pollCount > timeout) {
    return dispatch(getMakePaymentUnknownState());
  }
  try {
    await request.get(
      `${propertyId}/${orderNumber}/deferred-payments/${moneyTransferQueueId}/status`
    );

    return dispatch(getMakePaymentSuccess());
  } catch (error: any) {
    const { status, data } = error?.response || {};

    if (paymentMethod && data?.pmId !== paymentMethod) {
      await wait(1000);
      return awaitOrderStatus(
        propertyId,
        orderNumber,
        moneyTransferQueueId,
        paymentMethod,
        dispatch,
        pollCount + 1
      );
    }

    if (![402, 422].includes(status)) {
      throw error;
    }

    if (status === 422 && data?.clientSecret) {
      return dispatch(getMakePaymentCardDeclined(data.clientSecret));
    }

    await wait();
    return awaitOrderStatus(
      propertyId,
      orderNumber,
      moneyTransferQueueId,
      paymentMethod,
      dispatch,
      pollCount + 1
    );
  }
};

export const makePayment: MakePaymentType =
  ({
    propertyId,
    orderNumber,
    moneyTransferQueueId,
    stripe,
    elements,
    name,
    clientSecret,
    datatransRef,
    paymentProvider,
  }) =>
  async (dispatch) => {
    dispatch(getMakePaymentRequest());

    try {
      if (paymentProvider?.toLowerCase() === PaymentProviders.DATATRANS.toLowerCase()) {
        if (!datatransRef) {
          throw new Error('Datatrans payment form not initialized');
        }

        const expiryValues = datatransRef.getExpiryValues();
        const submitResult = await datatransRef.submit(expiryValues.month, expiryValues.year);

        if (submitResult.redirect) {
          window.location.href = submitResult.redirect;
          return;
        }

        await request.post(`${propertyId}/orders/${orderNumber}/authorize`, {
          provider: PaymentProviders.DATATRANS,
        });

        await awaitOrderStatus(propertyId, orderNumber, moneyTransferQueueId, null, dispatch);

        return;
      }

      if (!stripe || !elements) {
        throw new Error('Stripe not found');
      }

      if (!clientSecret) {
        throw new Error('Client secret not found');
      }

      const confirmParams = {
        return_url: `${window.location.origin}${Routes.CONFIRMATION}`,
        payment_method_data: {
          billing_details: { name },
        },
      };

      const isPaymentIntent = clientSecret.startsWith('pi_');

      let stripeResponse;
      let paymentMethod;

      if (isPaymentIntent) {
        stripeResponse = await stripe.confirmPayment({
          elements,
          confirmParams,
          redirect: 'if_required',
        });

        const { error, paymentIntent } = stripeResponse;
        if (error) {
          throw new Error(error.message);
        }
        paymentMethod = paymentIntent?.payment_method;
      } else {
        stripeResponse = await stripe.confirmSetup({
          elements,
          confirmParams,
          redirect: 'if_required',
        });

        const { error, setupIntent } = stripeResponse;
        if (error) {
          throw new Error(error.message);
        }
        paymentMethod = setupIntent?.payment_method;
      }

      await awaitOrderStatus(
        propertyId,
        orderNumber,
        moneyTransferQueueId,
        paymentMethod,
        dispatch
      );
    } catch (error) {
      dispatch(getMakePaymentFailure());
    }
  };
